/**
 * Drone Restricted Area Calculator - Calculation Utilities
 * 
 * This file contains core calculation functions for determining drone operation zones
 * based on flight parameters and regulatory guidelines.
 */

// Constants
export const GRAVITY = 9.8; // m/s²
export const FEET_TO_METERS = 0.3048; // Conversion factor: 1 foot = 0.3048 meters
export const METERS_TO_FEET = 3.28084; // Conversion factor: 1 meter = 3.28084 feet

/**
 * Calculate the soft geo-fence boundary based on altitude tiers
 * Updated buffer zones:
 * - 0-199 ft altitude: 5 ft buffer
 * - 200-299 ft altitude: 10 ft buffer
 * - 300-399 ft altitude: 15 ft buffer
 * - 400 ft altitude: 20 ft buffer
 * 
 * @param altitude - Flight altitude in current unit system
 * @param unitSystem - Current unit system ('metric' or 'imperial')
 * @returns Soft geo-fence distance in current unit system
 * @throws Error if altitude is negative
 */
export const calculateSoftGeoFence = (altitude: number, unitSystem = 'imperial'): number => {
  // Validate input parameters
  if (altitude < 0) {
    throw new Error('Altitude cannot be negative');
  }
  
  if (unitSystem !== 'metric' && unitSystem !== 'imperial') {
    throw new Error('Unit system must be either "metric" or "imperial"');
  }
  
  // Standardize to feet for the tiered calculation
  const altitudeInFeet = unitSystem === 'metric' ? altitude * METERS_TO_FEET : altitude;
  
  let bufferInFeet: number;
  
  if (altitudeInFeet < 200) {
    bufferInFeet = 5;
  } else if (altitudeInFeet < 300) {
    bufferInFeet = 10;
  } else if (altitudeInFeet < 400) {
    bufferInFeet = 15;
  } else {
    bufferInFeet = 20;
  }
  
  // Return in the appropriate unit system
  return unitSystem === 'metric' ? bufferInFeet * FEET_TO_METERS : bufferInFeet;
};

/**
 * Calculate the restricted area based on free fall physics
 * Formula: r = v * sqrt(2 * h / g)
 * 
 * @param velocity - Maximum velocity in m/s
 * @param altitude - Flight altitude in current unit system
 * @param unitSystem - Current unit system ('metric' or 'imperial')
 * @returns Restricted area radius in current unit system
 * @throws Error if velocity or altitude is negative
 */
export const calculateRestrictedArea = (velocity: number, altitude: number, unitSystem = 'imperial'): number => {
  // Validate input parameters
  if (velocity < 0) {
    throw new Error('Velocity cannot be negative');
  }
  
  if (altitude < 0) {
    throw new Error('Altitude cannot be negative');
  }
  
  if (unitSystem !== 'metric' && unitSystem !== 'imperial') {
    throw new Error('Unit system must be either "metric" or "imperial"');
  }
  
  // Convert altitude to meters for calculation if in imperial units
  const altitudeInMeters = unitSystem === 'imperial' ? altitude * FEET_TO_METERS : altitude;
  
  // Calculate result in meters
  const resultInMeters = velocity * Math.sqrt((2 * altitudeInMeters) / GRAVITY);
  
  // Return in the appropriate unit system
  return unitSystem === 'imperial' ? resultInMeters * METERS_TO_FEET : resultInMeters;
};

/**
 * Calculate altitude-based increase to restricted area
 * 33 feet (10 meters) per 100 feet of altitude
 * 
 * @param altitude - Flight altitude in current unit system
 * @param unitSystem - Current unit system ('metric' or 'imperial')
 * @returns Additional buffer in current unit system
 * @throws Error if altitude is negative
 */
export const calculateAltitudeIncrease = (altitude: number, unitSystem = 'imperial'): number => {
  // Validate input parameters
  if (altitude < 0) {
    throw new Error('Altitude cannot be negative');
  }
  
  if (unitSystem !== 'metric' && unitSystem !== 'imperial') {
    throw new Error('Unit system must be either "metric" or "imperial"');
  }
  
  // Standardize to feet for calculation
  const altitudeInFeet = unitSystem === 'imperial' ? altitude : altitude * METERS_TO_FEET;
  
  // Calculate 33 feet increase per 100 feet of altitude
  const increaseInFeet = (altitudeInFeet / 100) * 33;
  
  // Return in the appropriate unit system
  return unitSystem === 'imperial' ? increaseInFeet : increaseInFeet * FEET_TO_METERS;
};

/**
 * Calculate total restricted area
 * Uses the greater of two methods:
 * 1. Free-fall formula: r = v * sqrt(2 * h / g)
 * 2. 33ft per 100ft altitude increase
 * 
 * @param velocity - Maximum velocity in m/s
 * @param altitude - Flight altitude in current unit system
 * @param unitSystem - Current unit system ('metric' or 'imperial')
 * @returns Object with total restricted area values
 * @throws Error if velocity or altitude is negative
 */
export const calculateTotalRestrictedArea = (velocity: number, altitude: number, unitSystem = 'imperial') => {
  // Validate input parameters
  if (velocity < 0) {
    throw new Error('Velocity cannot be negative');
  }
  
  if (altitude < 0) {
    throw new Error('Altitude cannot be negative');
  }
  
  if (unitSystem !== 'metric' && unitSystem !== 'imperial') {
    throw new Error('Unit system must be either "metric" or "imperial"');
  }
  
  // Calculate using free-fall formula
  const restrictedArea1 = calculateRestrictedArea(velocity, altitude, unitSystem);
  
  // Calculate using 33ft/100ft rule
  const restrictedArea2 = calculateAltitudeIncrease(altitude, unitSystem);
  
  // Use whichever calculation yields the greater value
  const totalRestrictedArea = Math.max(restrictedArea1, restrictedArea2);
  
  // For display purposes, we may want to format differently
  const totalRestrictedAreaDisplay = Math.ceil(totalRestrictedArea);
  
  return { totalRestrictedArea, totalRestrictedAreaDisplay };
};

/**
 * Calculate hard geo-fence (always 1m from soft geo-fence boundary)
 * 
 * @param softGeoFence - Soft geo-fence distance in current unit system
 * @param unitSystem - Current unit system ('metric' or 'imperial')
 * @returns Hard geo-fence distance in current unit system
 * @throws Error if softGeoFence is negative
 */
export const calculateHardGeoFence = (softGeoFence: number, unitSystem = 'imperial'): number => {
  // Validate input parameters
  if (softGeoFence < 0) {
    throw new Error('Soft geo-fence distance cannot be negative');
  }
  
  if (unitSystem !== 'metric' && unitSystem !== 'imperial') {
    throw new Error('Unit system must be either "metric" or "imperial"');
  }
  
  // Hard geo-fence is always exactly 1 meter from soft geo-fence
  return unitSystem === 'imperial' ? METERS_TO_FEET : 1; // Return just the buffer width (1m)
};

/**
 * Convert meters to feet
 * 
 * @param meters - Distance in meters
 * @returns Distance in feet
 * @throws Error if meters is negative
 */
export const metersToFeet = (meters: number): number => {
  if (meters < 0) {
    throw new Error('Distance in meters cannot be negative');
  }
  return meters * METERS_TO_FEET;
};

/**
 * Convert values between unit systems
 * 
 * @param value - The value to convert
 * @param fromUnit - The unit system to convert from ('metric' or 'imperial')
 * @param toUnit - The unit system to convert to ('metric' or 'imperial')
 * @returns The converted value
 * @throws Error if value is negative or if unit systems are invalid
 */
export const convertBetweenUnits = (value: number, fromUnit: string, toUnit: string): number => {
  // Validate input parameters
  if (value < 0) {
    throw new Error('Value cannot be negative');
  }
  
  if (fromUnit !== 'metric' && fromUnit !== 'imperial') {
    throw new Error('Source unit system must be either "metric" or "imperial"');
  }
  
  if (toUnit !== 'metric' && toUnit !== 'imperial') {
    throw new Error('Target unit system must be either "metric" or "imperial"');
  }
  
  if (fromUnit === toUnit) {
    return value; // No conversion needed
  }
  
  if (fromUnit === 'imperial' && toUnit === 'metric') {
    return feetToMeters(value); // Convert feet to meters
  } else {
    return metersToFeet(value); // Convert meters to feet
  }
};

/**
 * Convert feet to meters
 * 
 * @param feet - Distance in feet
 * @returns Distance in meters
 */
export const feetToMeters = (feet: number): number => {
  return feet * FEET_TO_METERS;
};

/**
 * Check if calculated zones fit within available space
 * 
 * @param flightRadius - Radius of flight area in current unit system
 * @param totalRestrictedArea - Total calculated restricted area in current unit system
 * @param availableSpace - Available space radius in current unit system
 * @param unitSystem - Current unit system ('metric' or 'imperial')
 * @returns Boolean indicating if zones fit within available space
 */
export const checkSpaceConstraints = (
  flightRadius: number,
  totalRestrictedArea: number,
  availableSpace: number,
  unitSystem = 'imperial'
): boolean => {
  // Calculate total radius needed in current unit system
  // Total radius = flight area + soft geofence + hard geofence (1m) + restricted area
  const softGeoFence = calculateSoftGeoFence(availableSpace, unitSystem); // This is a simplification, should ideally use altitude
  const hardGeoFenceOffset = unitSystem === 'imperial' ? METERS_TO_FEET : 1; // 1 meter offset
  
  // Total radius from center of flight area to edge of restricted area
  const totalRadius = flightRadius + softGeoFence + hardGeoFenceOffset + totalRestrictedArea;
  
  // Check if total radius fits within available space
  return totalRadius <= availableSpace;
};
